import React, { Component } from "react";
import FormSection from "@my-dld/components/Form/FormSection";
import Strings from "@my-dld/assets/strings";
import { IdExtractorFallback, lookupConverter, serverDateConverter } from "@my-dld/utils/LookupUtil";
import { Lookup } from "@eres/ejari-api/Entity";
import { LoadableDataState } from "@my-dld/utils/UIUtils";
import {
	AlphaSortProcessor,
	isGovernmentAuthority,
	isLicStateAuthority,
	LicenseAuthoritiesEmiratesTransformer,
	LicSourceProcessor,
	populateLookupOnTo,
	StateConsumer
} from "@eres/ejari-api/Utils";
import { EjariLookUp } from "@eres/ejari-api/constants";
import { SpinnerLoader } from "@my-dld/components/SpinnerLoader";
import { EjariContractContext } from "@eres/ejari-core/contexts/EjariContractContext";
import { CompanyTenant, Tenant } from "@eres/ejari-api/Entity/tenant";
import _ from "lodash";
import { TenantForm } from "./index";
import FormRow from "@eres/forms-web/FormRow";
import StateHolder from "@eres/forms/StateHolder";
import { WebFormFieldProps } from "@eres/forms-web";
import { defaultErrorManager } from "@eres/forms/errormanager";
import { dualDataManager } from "@eres/forms/valuemanager/DualDataManager";
import TextInputField from "@eres/forms-web/TextInputField";
import { DisableEditOrgDataProvider } from "@eres/forms/providers/editable/OrgDataEditProvider";
import PickerInput from "@eres/forms-web/PickerInput";
import DateInputField from "@eres/forms-web/DateInputField";
import MobileInputField from "@eres/forms-web/MobileInputField";
import localMobileConverter from "@eres/forms/converters/LocalMobileConverter";
import { ErrorContainer } from "@eres/forms";
import { checkNonEmpty } from "../../../utils";
import { StringUtil } from "@eres/core";
import FormMandatory from "../../FormMandatory";
import NumberConverter from "@eres/forms/converters/NumberConverter";

interface Props {
	onDataChanged?: () => void;
}

interface Lookups {
	TenantTypes?: Lookup[];
	Emirates?: Lookup[];
	Authorities?: Lookup[];
	Countries?: Lookup[];
	LicenseStates?: Lookup[];
}

interface CompanyTenantX extends CompanyTenant {
	TenantArabicName?: string;
	TenantEnglishName?: string;
	Pobox?: string;
	Email?: string;
	TotalCoTenants?: number;
}

interface State extends Lookups, LoadableDataState {
	newData?: CompanyTenantX;
	orgData?: CompanyTenantX;
	showState: boolean;
	showCompanyName: boolean;
	TotalCoTenants?: number;
}

class CompanyTenantForm extends Component<Props, State> implements TenantForm {
	static contextType = EjariContractContext;
	context!: React.ContextType<typeof EjariContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			loadState: "loading",
			errorContainer: {},
			showState: false,
			showCompanyName: false,
		};
	}

	validate = () => {
		let isValid = true;
		const errorContainer: ErrorContainer = {};
		const nd = this.getUpdatedData();
		const { isCommercial } = this.context;
		const { showCompanyName, showState } = this.state;
		isValid = isValid && checkNonEmpty( nd, "LicenseIssuerEmirate", errorContainer );
		isValid = isValid && checkNonEmpty( nd, "LicenseIssuer", errorContainer );
		if ( !showCompanyName ) {
			isValid = isValid && checkNonEmpty( nd, "LicenseNo", errorContainer );
			isValid = isValid && checkNonEmpty( nd, "LicenseIssueDate", errorContainer );
			isValid = isValid && checkNonEmpty( nd, "LicenseExpiryDate", errorContainer );
		}
		if ( showState )
			isValid = isValid && checkNonEmpty( nd, "LicenseStateId", errorContainer );
		isValid = isValid && checkNonEmpty( nd, "TenantEnglishName", errorContainer );
		isValid = isValid && checkNonEmpty( nd, "TenantArabicName", errorContainer );
		isValid = isValid && checkNonEmpty( nd, "ContactPersonName", errorContainer );
		isValid = isValid && checkNonEmpty( nd, "Pobox", errorContainer );
		isValid = isValid && ( isCommercial || checkNonEmpty( nd, "TotalCoTenants", errorContainer ) );
		if ( isValid && !isCommercial && nd.TotalCoTenants ) {
			isValid = nd?.TotalCoTenants <= this.context.AllowedCoTenants!;
			if ( !isValid ) {
				errorContainer[ "TotalCoTenants" ] = Strings.allowed_co_tenants.format( this.context.AllowedCoTenants!.toString() );
			}
		}
		let validEmail = checkNonEmpty( nd, "Email", errorContainer );
		if ( validEmail ) {
			validEmail = validEmail && StringUtil.isEmailAddress( nd.Email! );
			if ( !validEmail ) {
				errorContainer[ "Email" ] = Strings.invalid_email_address;
			}
		}
		isValid = isValid && validEmail;

		let validMobile = checkNonEmpty( nd, "ContactPersonMobile", errorContainer );
		if ( validMobile ) {
			let normaliseMobileNumber = StringUtil.normaliseMobileNumber( nd.ContactPersonMobile! );
			validMobile = validMobile && normaliseMobileNumber.length >= 12;
			if ( !validMobile ) {
				errorContainer[ "ContactPersonMobile" ] = Strings.invalid_mobile_number;
			}
		}
		isValid = isValid && validMobile;

		this.setState( {
			errorContainer: errorContainer,
		} );
		return isValid;
	};
	updateTenant = ( tenant: Tenant ) => {
		const nd = this.getUpdatedData();
		const newT: Tenant = _.cloneDeep( tenant );
		newT.TenantEnglishName = nd.TenantEnglishName;
		newT.TenantArabicName = nd.TenantArabicName;
		newT.Pobox = nd.Pobox;
		newT.Email = nd.Email;
		newT.IsCompany = true;
		newT.Company = _.omit( nd, [ "TenantArabicName", "TenantEnglishName", "Pobox", "Email", "TotalCoTenants" ] );
		newT.Person = undefined;
		newT.hasLicState = this.state.showState;
		newT.isGovernment = this.state.showCompanyName;
		newT.TotalCoTenants = this.state.TotalCoTenants;
		return newT;
	};

	updateTotalCoTenants = () => {
		const nd = this.getUpdatedData();
		return nd.TotalCoTenants ?? 0;
	};

	componentDidMount() {
		populateLookupOnTo( EjariLookUp.TENANT_TYPES, StateConsumer( this, "TenantTypes" ) );
		populateLookupOnTo( EjariLookUp.EMIRATES, StateConsumer( this, "Emirates" ), LicenseAuthoritiesEmiratesTransformer, LicSourceProcessor );
		populateLookupOnTo( EjariLookUp.LICENSE_STATES, StateConsumer( this, "LicenseStates" ), undefined, AlphaSortProcessor );
		const { OrgTenant, tenant } = this.context;
		const data: CompanyTenant = {};
		_.merge( data, OrgTenant?.Company, tenant?.Company, { TotalCoTenants: this.context.contract?.TotalCoTenants } );
		const xT: CompanyTenantX = {
			...tenant?.Company,
			TotalCoTenants: this.context.contract?.TotalCoTenants,
		};
		if ( tenant?.TenantEnglishName )
			xT.TenantEnglishName = tenant?.TenantEnglishName;
		if ( tenant?.TenantArabicName )
			xT.TenantArabicName = tenant?.TenantArabicName;
		if ( tenant?.Pobox )
			xT.Pobox = tenant?.Pobox;
		if ( tenant?.Email )
			xT.Email = tenant?.Email;
		this.setState( {
			loadState: "done",
			orgData: {
				...OrgTenant?.Company,
				TenantArabicName: OrgTenant?.TenantArabicName,
				TenantEnglishName: OrgTenant?.TenantEnglishName,
				Pobox: OrgTenant?.Pobox,
				Email: OrgTenant?.Email,
				TotalCoTenants: OrgTenant?.TotalCoTenants,
			},
			newData: xT,
		} );
	}

	updateLicenseAuthorities = ( emId: number ) => {
		const { Emirates } = this.state;
		const x = Emirates?.find( x => x.Id === emId );
		if ( x ) {
			this.setState( {
				Authorities: x.Children,
			} );
		}
	};
	updateFields = ( authId: string ) => {
		const { Authorities } = this.state;
		if ( Authorities ) {
			const Authority = Authorities.find( x => ( x.Id + "" ) === authId + "" );
			if ( Authority ) {
				this.setState( {
					showState: isLicStateAuthority( Authority ),
					showCompanyName: isGovernmentAuthority( Authority ),
				} );
			} else {
				this.setState( {
					showState: false,
					showCompanyName: false,
				} );
			}
		}

	};
	dispatchUpdate = () => {
		this.props.onDataChanged?.call( this );
	};

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		const nd = this.getUpdatedData();
		const od = this.getUpdatedData( prevState );
		if ( this.state.Emirates !== prevState.Emirates || ( nd.LicenseIssuerEmirate !== od.LicenseIssuerEmirate ) ) {
			this.updateLicenseAuthorities( nd.LicenseIssuerEmirate! );
		}
		if ( this.state.Authorities !== prevState.Authorities || !_.isEqual( nd.LicenseIssuer, od.LicenseIssuer ) ) {
			this.updateFields( nd.LicenseIssuer! );
		}
		if ( !_.isEqual( prevState, this.state ) )
			this.dispatchUpdate();
	}

	render() {
		const { loadState, } = this.state;
		const { isCommercial } = this.context;
		if ( loadState === "loading" )
			return <SpinnerLoader/>;
		const {
			Emirates, Authorities, LicenseStates,
			showState, showCompanyName,
		} = this.state;
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
			errorManager: defaultErrorManager,
		};
		return <>
			<FormSection title={ Strings.company_information }>
				<FormMandatory/>
				<FormRow { ...filedProps }>
					<PickerInput
						required={ true }
						isLoading={ Emirates === undefined }
						id={ "LicenseIssuerEmirate" }
						toIdentifier={ IdExtractorFallback }
						valueConverter={ lookupConverter }
						valueManager={ dualDataManager }
						enableEditProvider={ DisableEditOrgDataProvider }
						label={ Strings.lic_issuer_emirate }
						items={ Emirates }
					/>
					<PickerInput
						required={ true }
						isLoading={ Authorities === undefined }
						id={ "LicenseIssuer" }
						toIdentifier={ IdExtractorFallback }
						valueConverter={ lookupConverter }
						valueManager={ dualDataManager }
						enableEditProvider={ DisableEditOrgDataProvider }
						label={ Strings.lic_issuer_authority }
						items={ Authorities }
					/>
				</FormRow>

				{ showCompanyName == false && <>
					<FormRow { ...filedProps }>
						<TextInputField
							required={ true }
							id={ "LicenseNo" }
							valueManager={ dualDataManager }
							enableEditProvider={ DisableEditOrgDataProvider }
							label={ Strings.lic_number }
						/>

						<DateInputField
							required={ true }
							label={ Strings.lic_issue_date }
							id={ "LicenseIssueDate" }
							valueManager={ dualDataManager }
							valueConverter={ serverDateConverter }
						/>
						<DateInputField
							required={ true }
							label={ Strings.lic_exp_date }
							id={ "LicenseExpiryDate" }
							valueManager={ dualDataManager }
							valueConverter={ serverDateConverter }
						/>
					</FormRow>
				</> }
				<FormRow { ...filedProps }>
					{ showState &&
						<PickerInput
							required={ true }
							isLoading={ LicenseStates === undefined }
							id={ "LicenseStateId" }
							toIdentifier={ IdExtractorFallback }
							valueConverter={ lookupConverter }
							valueManager={ dualDataManager }
							enableEditProvider={ DisableEditOrgDataProvider }
							label={ Strings.license_state }
							items={ LicenseStates }
						/> }
					<TextInputField
						required={ true }
						extraInputClassName={ "en-text" }
						id={ "TenantEnglishName" }
						valueManager={ dualDataManager }
						enableEditProvider={ DisableEditOrgDataProvider }
						label={ Strings.company_name_en }
					/>
					<TextInputField
						required={ true }
						extraInputClassName={ "ar-text" }
						type={ "arabic" }
						id={ "TenantArabicName" }
						valueManager={ dualDataManager }
						enableEditProvider={ DisableEditOrgDataProvider }
						label={ Strings.company_name_ar }
					/>
				</FormRow>
			</FormSection>
			<FormSection title={ Strings.contact_information } extraClass={ "mt-2" }>
				<FormMandatory/>
				<FormRow { ...filedProps }>
					<TextInputField
						required={ true }
						id={ "ContactPersonName" }
						valueManager={ dualDataManager }
						label={ Strings.contact_name }
					/>
					<TextInputField
						required={ true }
						id={ "Email" }
						valueManager={ dualDataManager }
						label={ Strings.email }
					/>
					<MobileInputField
						required={ true }
						id={ "ContactPersonMobile" }
						valueConverter={ localMobileConverter }
						valueManager={ dualDataManager }
						label={ Strings.mobile_number }
					/>
					<TextInputField
						required={ true }
						id={ "Pobox" }
						valueManager={ dualDataManager }
						label={ Strings.po_box }
					/>
					{ !isCommercial && <TextInputField
						required={ true }
						id={ "TotalCoTenants" }
						valueManager={ dualDataManager }
						valueConverter={ NumberConverter }
						label={ Strings.no_of_co_occupants }
					/> }
				</FormRow>
			</FormSection>
		</>;
	}

	getUpdatedData = ( state = this.state ) => {
		const data: CompanyTenantX = {};
		_.merge( data, state.orgData, state.newData );
		return data;
	};
}

export default CompanyTenantForm;
